import React, { useState, useEffect } from "react";
import { useParams } from "react-router";
import { useSmartNavigateLegacy as useNavigate } from "components/SmartLink";
import { Tab, Tabs, Button as BsButton } from "react-bootstrap";
import {
  Wrapper,
  PublishButton,
  SaveButton,
  BackButton,
  MobileContainerPadding,
} from "../create/index.style";
import { TrackInfoFormCard } from "../create/components/trackInfoFormCard/TrackInfoFormCard";
import { TrackLessonsFormCard } from "../create/components/trackLessonsFormCard/TrackLessonsFormCard";
import { useTrackInfo } from "../create/hooks/useTrackInfo";
import { useLessons } from "../create/hooks/useLessons";
import { TrackDraft } from "../create/types";
import {
  trackService,
  TrackDetailResponse
} from "data/services/trackService";
import { showToast } from "components/toast";
import { Loading } from "components/loading";
import { useTrackEdit } from "./hooks/useTrackEdit";
import { UploadProgress } from "../create/components/UploadProgress";
import { showConfirmationPopUp } from "components/popup/confirmationModal";
import { formatVideoUrl, validateLesson } from "../create/components/trackLessonsFormCard/TrackLessonsFormCard";
import { TabHeader } from "../../../../../domain/feature/hub/company/challenge/program/components/tabHeader/tabHeader";

export const EditTrackForm: React.FC = () => {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const [activeKey, setActiveKey] = useState<"info" | "lessons">("info");
  const [trackData, setTrackData] = useState<TrackDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const defaultTrackData: TrackDraft = {
    title: "",
    description: "",
    imageUrl: "",
  };

  const {
    draft,
    setDraft,
    errors,
    fileRef,

    validate,
    handleFile,
    triggerUpload,
    removeImage,
  } = useTrackInfo(defaultTrackData);

  const {
    lessons,
    setLessons,
    patchLesson,
    moveDown,
    moveUp,
    duplicate,
    removeLesson,
    addLesson,
  } = useLessons([]);

  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const {
    handleUpdateFlow,
    isLoading: isUploading,
    uploadProgress,
    uploadError,
    isUploading: isVideoUploading,
  } = useTrackEdit();

  useEffect(() => {
    const fetchTrackData = async () => {
      if (!trackId) return;

      try {
        setIsLoading(true);
        setError(null);
        const data = await trackService.getTrackDetails(trackId);
        setTrackData(data);

        const trackDraft: TrackDraft = {
          title: data.title,
          description: data.description || "",
          imageUrl: data.imageUrl || "",
        };
        setDraft(trackDraft);

        const formattedLessons = data.lessons.map((lesson) => {
          return {
            id: lesson.id,
            title: lesson.title,
            description: lesson.description || "",
            videoId: lesson.videoId || "",
            videoProvider: lesson.videoProvider,
            minutes: lesson.minutes,
            sequence: lesson.sequence,
            allowQuestions: lesson.permissions?.allowQuestions || false,
            allowFiles: lesson.permissions?.allowFileUploads || false,
            originalVideoId: lesson.videoId || "",
            originalvideoProvider: lesson.videoProvider,
            videoUrl: lesson.videoId
              ? formatVideoUrl(lesson.videoProvider, lesson.videoId)
              : null,
          };
        });

        setLessons(formattedLessons);
      } catch (err: any) {
        console.error("Erro ao buscar dados da trilha:", err);
        setError(err?.response?.data?.message || "Erro ao carregar trilha");
      } finally {
        setIsLoading(false);
      }
    };

    void fetchTrackData();
  }, [trackId, setDraft]);

  const save = async () => {
    if (!validate() || !trackId || !trackData) {
      setActiveKey("info");
      return;
    }

    const hasValidLessons =
      lessons.length > 0 &&
      lessons.every(
        (lesson) => validateLesson(lesson)
      );

    if (!hasValidLessons) {
      setActiveKey("lessons");
      showToast(
        "error",
        "Por favor, preencha todas as informações das aulas antes de salvar."
      );
      return;
    }

    try {
      setIsSaving(true);

      const videoFiles: { [lessonIndex: number]: File } = {};
      lessons.forEach((lesson, index) => {
        if (lesson.videoFile) {
          videoFiles[index] = lesson.videoFile;
        }
      });

      await handleUpdateFlow(
        trackId,
        draft,
        lessons,
        trackData.status === "Publish" ? "Publish" : "Draft",
        videoFiles
      );

      const updatedData = await trackService.getTrackDetails(trackId);
      setTrackData(updatedData);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao salvar trilha. Tente novamente.";
      showToast("error", errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const publish = async () => {
    if (!validate() || !trackId) {
      setActiveKey("info");
      return;
    }

    const hasValidLessons =
      lessons.length > 0 &&
      lessons.every(
        (lesson) =>
          lesson.title.trim().length > 0 && lesson.description.trim().length > 0
      );

    if (!hasValidLessons) {
      setActiveKey("lessons");
      showToast(
        "error",
        "Por favor, preencha todas as informações das aulas antes de publicar."
      );
      return;
    }

    try {
      setIsPublishing(true);

      const response = await trackService.togglePublishTrack(trackId);

      const newStatus = response.status === "Publish" ? "Publish" : "Draft";
      setTrackData((prev) => (prev ? { ...prev, status: newStatus } : null));

      if (newStatus === "Publish") {
        showToast("success", "Trilha publicada com sucesso!");
      } else {
        showToast("success", "Trilha voltou para rascunho");
      }
    } catch (error: any) {
      console.error("Erro ao alterar status da trilha:", error);
      showToast(
        "error",
        error?.response?.data?.message ||
        "Erro ao alterar status da trilha. Tente novamente."
      );
    } finally {
      setIsPublishing(false);
    }
  };

  const confirmPublish = async () => {
    if (trackData?.status === "Publish") {
      showConfirmationPopUp({
        title: "Salvar e publicar",
        message:
          "Tem certeza de que deseja salvar as alterações e manter a trilha publicada?",
        onConfirm: async () => {
          await save();
        },
      });
    } else {
      showConfirmationPopUp({
        title: "Publicar trilha",
        message: "Tem certeza de que deseja publicar trilha?",
        onConfirm: async () => {
          await publish();
        },
      });
    }
  };

  const goNext = () => {
    if (!validate()) {
      setActiveKey("info");
      return;
    }
    setActiveKey("lessons");
  };

  const goBack = () => {
    navigate("/hub/tracks");
  };

  if (isLoading) {
    return (
      <Wrapper>
        <Loading />
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <div className="text-center py-5">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Erro ao carregar trilha</h4>
            <p>{error}</p>
            <hr />
            <button
              className="btn btn-outline-primary"
              onClick={() => navigate("/hub/tracks")}
            >
              Voltar para trilhas
            </button>
          </div>
        </div>
      </Wrapper>
    );
  }

  function creatNextButton() {
    return <div className="d-flex justify-content-between mt-4">
      <SaveButton
        onClick={goNext}
        disabled={
          isSaving || isPublishing || isUploading || isVideoUploading
        }
        style={{
          opacity:
            isSaving || isPublishing || isUploading || isVideoUploading
              ? 0.5
              : 1,
          cursor:
            isSaving || isPublishing || isUploading || isVideoUploading
              ? "not-allowed"
              : "pointer",
        }}
      >
        Próximo
      </SaveButton>
      {createTopButtons()}
    </div>;
  }

  function createTopButtons() {
    return <div className="d-flex justify-content-end gap-2">

      {trackData?.status === "Publish" ? (
        <PublishButton
          onClick={confirmPublish}
          disabled={
            isPublishing || isSaving || isUploading || isVideoUploading
          }
        >
          {isPublishing || isSaving || isUploading || isVideoUploading
            ? "Processando..."
            : "Salvar e publicar"}
        </PublishButton>
      ) : (
        <>
          <SaveButton
            onClick={save}
            disabled={
              isSaving || isPublishing || isUploading || isVideoUploading
            }
          >
            {isSaving || isUploading || isVideoUploading
              ? "Processando..."
              : "Salvar"}
          </SaveButton>
          <PublishButton
            onClick={confirmPublish}
            disabled={
              isPublishing || isSaving || isUploading || isVideoUploading
            }
          >
            {isPublishing || isUploading || isVideoUploading
              ? "Processando..."
              : "Publicar"}
          </PublishButton>
        </>
      )}
    </div>;
  }

  return (
    <>
      <MobileContainerPadding />
      <Wrapper>
      <div className="mb-2">
        <div
          style={{
            fontFamily: "sans-serif",
            fontWeight: 700,
            fontSize: 28,
            lineHeight: "120%",
            letterSpacing: 0,
            color: "#464646",
          }}
        >
          Editar trilha
        </div>
      </div>


      <UploadProgress
        isVisible={isUploading}
        progress={uploadProgress}
        error={uploadError}
        message="Atualizando trilha..."
      />

      <Tabs
        id="edit-track-tabs"
        activeKey={activeKey}
        onSelect={(k) => {
          if (!isSaving && !isPublishing && !isUploading && !isVideoUploading) {
            k && setActiveKey(k as any);
          }
        }}
      >
        <Tab eventKey="info" title={<TabHeader itemKey="1" title="Informações Gerais" />}>
          <TrackInfoFormCard
            draft={draft}
            errors={errors}
            fileRef={fileRef}
            onChange={(field, value) => setDraft({ ...draft, [field]: value })}
            handleFile={handleFile}
            triggerUpload={triggerUpload}
            removeImage={removeImage}
          />
          {creatNextButton()}
        </Tab>

        <Tab eventKey="lessons" title={<TabHeader itemKey="2" title="Aulas" />}>
          {lessons.map((lesson, i) => {
            return (
              <div key={lesson.id} className="mb-3">
                <TrackLessonsFormCard
                  index={i}
                  lesson={lesson}
                  onChange={(patch) => patchLesson(i, patch)}
                  onMoveUp={() => moveUp(i)}
                  onMoveDown={() => moveDown(i)}
                  onDuplicate={() => duplicate(i)}
                  onDelete={() => removeLesson(i)}
                  onAddLesson={i === lessons.length - 1 ? addLesson : undefined}
                />
              </div>
            );
          })}

          <div className="d-flex justify-content-between mt-4">
            <BackButton onClick={() => setActiveKey("info")}>
              Voltar
            </BackButton>
            {createTopButtons()}
          </div>

        </Tab>
      </Tabs>
    </Wrapper>
    </>
  );
};
